import {Loading} from "@mytour/ui/Loading";
import {Suspense} from "react";
import {Navigate, createBrowserRouter} from "react-router";

import {
  lazyWithReload,
  useContext,
  useIsMultiUser,
  useTrackPlanningViewed,
} from "@mytour/platform";
import {isMobile} from "react-device-detect";
import {HomePageRedirect} from "../pages/HomePageRedirect";
import {RootLayout} from "../pages/RootLayout";
import {AutoLoginPage} from "../pages/login/AutoLoginPage";
import {GoogleAuthPage} from "../pages/login/GoogleAuthPage";
import {LogoutPage} from "../pages/login/LogoutPage";
import {ModerationLoginPage} from "../pages/login/ModerationLoginPage";
import {StandardLoginPage} from "../pages/login/StandardLoginPage";
import {PrivateRouteWrapper} from "./PrivateRouteWrapper";

function PlanningSwitch() {
  const {context} = useContext();
  useTrackPlanningViewed(context?.companyId);
  if (!context) {
    return null;
  }
  if (context.role === "worker") {
    return (
      <Suspense fallback={<Loading />}>
        <WorkerPlanningRoutes />
      </Suspense>
    );
  }
  return <ManagerPlanningSwitch />;
}

export function ManagerPlanningSwitch() {
  const isMultiUser = useIsMultiUser();
  if (isMultiUser && !isMobile) {
    return <MultiPlanningRoutes />;
  }
  return <SoloPlanningRoutes />;
}

const HomePage = lazyWithReload(() => import("../pages/roles/multi-manager/home/HomePage"));

const AccountRoutes = lazyWithReload(() => import("@mytour/platform/AccountRoutes"));
const ExecutionRoutes = lazyWithReload(() => import("@mytour/execution/ExecutionRoutes"));
const WorkerPlanningRoutes = lazyWithReload(
  () => import("@mytour/planning-worker/WorkerPlanningRoutes")
);
const SoloPlanningRoutes = lazyWithReload(
  () => import("@mytour/planning-solo/SoloPlanningRoutes")
);
const MultiPlanningRoutes = lazyWithReload(
  () => import("@mytour/planning-multi/MultiPlanningRoutes")
);
const ClientRoutes = lazyWithReload(() => import("@mytour/client-records/ClientRoutes"));
const BillingRoutes = lazyWithReload(() => import("@mytour/billing/BillingRoutes"));
const AccountingRoutes = lazyWithReload(() => import("@mytour/accounting/AccountingRoutes"));
const CompanySettingsRoutes = lazyWithReload(
  () => import("@mytour/company-settings/CompanySettingsRoutes")
);
const PageNotFound = lazyWithReload(() => import("../pages/PageNotFound"));

// Design-system gallery (DOCTRINE.md §3) — dev-only reference surface, not
// linked in the nav, reachable by URL only. Mounted as a plain public child of
// RootLayout (no PrivateRouteWrapper), like /login: RootLayout only renders a
// header when the visitor has a role, so an anonymous visitor sees just the
// gallery with no auth required and no bypass hack.
const DesignSystemPage = lazyWithReload(() => import("../pages/design-system/DesignSystemPage"));

// Public RDV booking tunnel (#429) — dedicated chunk, mounted as a SISTER route
// of "/" (outside RootLayout): no authenticated header, no session hook, noindex.
const BookingTunnelPage = lazyWithReload(() => import("@mytour/rdv-public/BookingTunnelPage"));

/**
 * Route sœur de "/", hors RootLayout, hors PrivateRouteWrapper (qui redirige
 * justement un contexte en veille ici). RootLayout n'est pas non plus une
 * option : il monte ManagerModals, qui appelle des routes `@Roles` et lèverait
 * le même 403 (#1357).
 */
const CompteEnVeillePage = lazyWithReload(
  () => import("../pages/compte-en-veille/CompteEnVeillePage")
);

// Pro action page for online booking requests (#429, Story 4.1a) — landing of
// the notification email's deep-link, authenticated manager route.
const RdvProActionPage = lazyWithReload(() => import("@mytour/rdv-pro/RdvProActionPage"));

// "Demandes" hub list (#429, Story 4.1b) — in-app list + landing of the daily
// relance email (4.5 points at /rdv-requests), authenticated manager route.
const RdvProListPage = lazyWithReload(() => import("@mytour/rdv-pro/RdvProListPage"));

// Create the router configuration
export function createAppRouter() {
  const href = document?.querySelector("base")?.getAttribute("href")?.replace(/\/$/, "");
  const baseHref = href || null;
  return createBrowserRouter(
    [
      // Public booking tunnel (#429): sister route of "/", OUTSIDE RootLayout, so
      // it never mounts the authenticated header or any session hook. The public
      // page `/pro/:slug` is server-rendered (SEO); `/pro/:slug/reserver` falls
      // through to this SPA chunk (see serverStatic.module.ts `SPA_RENDER_PATH`).
      // Splat so any nested path under `reserver` resolves here too: the server
      // already serves the SPA shell for `/pro/:slug/reserver/*`, and without the
      // splat such a path would fall through to the authenticated `*` catch-all
      // below and redirect an anonymous visitor to /login.
      {
        path: "pro/:slug/reserver/*",
        element: (
          <Suspense fallback={<Loading />}>
            <BookingTunnelPage />
          </Suspense>
        ),
      },
      {
        path: "compte-en-veille",
        element: (
          <Suspense fallback={<Loading />}>
            <CompteEnVeillePage />
          </Suspense>
        ),
      },
      {
        path: "/",
        element: <RootLayout />,
        children: [
          {
            path: "invitation/:invitationKey",
            element: <ModerationLoginPage />,
          },
          {
            path: "onboarding/:invitationKey",
            element: <AutoLoginPage />,
          },
          {
            path: "login",
            element: <StandardLoginPage />,
          },
          {
            path: "google/auth",
            element: <GoogleAuthPage />,
          },
          {
            path: "logout",
            element: <LogoutPage />,
          },
          {
            path: "design-system",
            element: (
              <Suspense fallback={<Loading />}>
                <DesignSystemPage />
              </Suspense>
            ),
          },
          {
            index: true,
            element: <HomePageRedirect />,
          },

          /** Manager routes */
          {
            path: "event/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <ExecutionRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          // Routes for managers only
          {
            path: "home",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <HomePage />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "account/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <AccountRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "customer/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <ClientRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          // Online booking requests hub list (#429, 4.1b): the daily relance
          // email deep-links here (PR #601 points at /rdv-requests).
          {
            path: "rdv-requests",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <RdvProListPage />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          // Online booking request action page (#429, 4.1a): the notification
          // email deep-links here — an unauthenticated click goes through
          // /login then redirects back (PrivateRouteWrapper's `from` state).
          {
            path: "rdv-requests/:requestId",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <RdvProActionPage />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "service-description/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Navigate to="/company-settings/my-business?tab=services" replace />
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "invoice/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <BillingRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "reports/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <AccountingRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
          {
            path: "company-settings/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager"]}>
                <Suspense fallback={<Loading />}>
                  <CompanySettingsRoutes />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },

          /** Planning routes (worker + manager) */
          {
            path: "planning/*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager", "worker"]}>
                <Suspense fallback={<Loading />}>
                  <PlanningSwitch />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },

          /** Catch-all route for 404 */
          {
            path: "*",
            element: (
              <PrivateRouteWrapper requiredRoles={["manager", "worker"]}>
                <Suspense fallback={<Loading />}>
                  <PageNotFound />
                </Suspense>
              </PrivateRouteWrapper>
            ),
          },
        ],
      },
    ],
    {
      basename: baseHref || undefined,
    }
  );
}
